import { type AdminSelf, isApiError } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatTimestamp,
  formatToman,
  MOBILE_E164_PATTERN,
  MOBILE_MASK,
  normalizeMobileField,
  statusLabel,
} from '@saay/formatting';
import {
  AccordionPanel,
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  EmptyState,
  ErrorState,
  FactList,
  FieldErrorList,
  IconButton,
  LoadMore,
  Ltr,
  Masked,
  Menu,
  MenuItem,
  MoneyField,
  SegmentedButtons,
  SegmentedControl,
  SheetSection,
  Skeleton,
  StatusChip,
  Table,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { useApiClient } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { formatLocalMobileDisplay } from '../display.js';
import { useSubmitBlocked } from '../components/form.js';

import type {
  AdminBuyer,
  AdminCreditAllocation,
  BaseCreditAdjustment,
  BuyerIdentityVerification,
  BuyerMobileChange,
  BuyerProvisioningPolicyVersion,
  BuyerRestriction,
  BuyerRestrictionCreate,
  BuyerStatusTransition,
  BuyerUpdate,
  CreditAllocationAdjusted,
  CreditAllocationAdjustmentRequest,
  CreditPolicyVersion,
} from '../api.js';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { CommandDialog } from '../components/command-dialog.js';
import { QueryState } from '../components/guard.js';
import { GeographyFields } from '../components/pickers.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { approvalSubmitLabel, canUse } from '../permissions.js';
import {
  listStatus,
  setupKeys,
  useAdminSelf,
  useBuyerRestrictions,
  useCurrentPolicy,
} from '../queries.js';
import { addCalendarDays, reasonSchema, startOfBusinessDay, tehranToday } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * The buyer drawer's commands (AC §18.6 B1–B7, B9, B10; FTD §9.4): profile edit, identity
 * verification, mobile change, status transitions, manual restrictions, and the two credit changes.
 * Every result is the server's (`AdminBuyer`, `BuyerRestriction`, `CreditAllocationAdjusted`, or a
 * `202 ApprovalPending`); nothing is applied on screen before the server confirmed it.
 */

function buyerPath(buyerId: string, tail = ''): string {
  return `/admin/buyers/${encodeURIComponent(buyerId)}${tail}`;
}

export function useRefreshBuyer(buyerId: string) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.buyer(buyerId) }),
      queryClient.invalidateQueries({ queryKey: setupKeys.buyers }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
    ]);
  };
}

/** Codes after which the drawer reloads the buyer (it changed meanwhile). */
const RELOAD_CODES = new Set(['RESOURCE_VERSION_CONFLICT', 'STATE_CONFLICT']);

/** The recovery line of a refused buyer command, by error code only (AC §18.6, §13). */
function buyerGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'BUYER_CLOSURE_DEBT_OUTSTANDING':
      return typeof details.outstandingIrr === 'number'
        ? `بدهی باز خریدار ${formatToman(details.outstandingIrr)} است؛ بستن حساب پس از تسویهٔ کامل ممکن است.`
        : 'تا تسویهٔ کامل بدهی، حساب بسته نمی‌شود.';
    case 'STATE_CONFLICT':
      return typeof details.currentStatus === 'string'
        ? `وضعیت فعلی: ${statusLabel('buyerStatus', details.currentStatus)}. اطلاعات به‌روز شد.`
        : 'اطلاعات به‌روز شد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'خریدار در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد. دوباره ثبت کنید.';
    case 'RESTRICTION_ALREADY_ACTIVE':
      return 'یک محدودیت دستی فعال وجود دارد؛ اول آن را رفع کنید.';
    case 'RESTRICTION_NOT_RELEASABLE':
      return typeof details.sourceType === 'string'
        ? `محدودیت «${statusLabel('buyerRestrictionSourceType', details.sourceType)}» با رفع دستی برداشته نمی‌شود.`
        : 'فقط محدودیت دستی رفع می‌شود.';
    case 'CREDIT_POLICY_LIMIT_EXCEEDED':
      return typeof details.limitIrr === 'number'
        ? `سقف سیاست اعتبار: ${formatToman(details.limitIrr)}.`
        : undefined;
    case 'CREDIT_PROGRAM_EXPOSURE_CAP_EXCEEDED':
      return typeof details.capIrr === 'number'
        ? `سقف تعهد برنامه ${formatToman(details.capIrr)} است؛ این افزایش از آن عبور می‌کند.`
        : undefined;
    case 'CREDIT_ALLOCATION_NOT_ADJUSTABLE':
      return 'فقط تخصیص اعتبار اضافی فعال یا زمان‌بندی‌شده اصلاح می‌شود؛ اعتبار پایه را از «اصلاح اعتبار پایه» تغییر دهید.';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------------------------
// B1 updateBuyer

const nameSchema = z.string().trim().min(1, 'را وارد کنید.').max(100, 'حداکثر ۱۰۰ کاراکتر.');

const editSchema = z.object({
  birthDate: z
    .string()
    .min(1, 'تاریخ تولد را وارد کنید.')
    .refine((value) => value < tehranToday(), 'تاریخ تولد باید در گذشته باشد.'),
  cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
  firstNameFa: nameSchema,
  lastNameFa: nameSchema,
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
  reason: reasonSchema(),
});
type EditValues = z.infer<typeof editSchema>;

function BuyerEditDialog({
  buyer,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileDisplay =
    buyer.mobileE164 === undefined ? (
      <Masked kind="mobile" value={buyer.mobileMasked} />
    ) : (
      <Ltr>{formatLocalMobileDisplay(buyer.mobileE164)}</Ltr>
    );
  return (
    <CommandDialog<EditValues, BuyerUpdate, AdminBuyer>
      defaultValues={{
        birthDate: buyer.birthDate,
        cityId: buyer.city.id,
        firstNameFa: buyer.firstNameFa,
        lastNameFa: buyer.lastNameFa,
        provinceId: buyer.province.id,
        reason: '',
      }}
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      fields={['firstNameFa', 'lastNameFa', 'birthDate', 'provinceId', 'cityId', 'reason']}
      guidance={buyerGuidance}
      name="buyer.update"
      onError={async (error, form) => {
        if (error.code === 'GEOGRAPHY_MISMATCH') {
          form.setError('cityId', { message: error.messageFa, type: 'server' });
        }
        if (RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'مشخصات خریدار به‌روز شد' });
        onOpenChange(false);
      }}
      schema={editSchema}
      send={(client, body) => client.patch(buyerPath(buyer.buyerId), { body })}
      submitLabel="ذخیره مشخصات"
      title="ویرایش مشخصات خریدار"
      toBody={(values) => {
        const changes: Partial<BuyerUpdate> = {};
        if (values.firstNameFa !== buyer.firstNameFa) changes.firstNameFa = values.firstNameFa;
        if (values.lastNameFa !== buyer.lastNameFa) changes.lastNameFa = values.lastNameFa;
        if (values.birthDate !== buyer.birthDate) changes.birthDate = values.birthDate;
        if (values.provinceId !== buyer.province.id || values.cityId !== buyer.city.id) {
          changes.provinceId = values.provinceId;
          changes.cityId = values.cityId;
        }
        return Object.keys(changes).length === 0
          ? null
          : { ...changes, expectedVersion: buyer.version, reason: values.reason };
      }}
    >
      {(form) => (
        <>
          <FactList
            items={[
              {
                key: 'national',
                label: 'کد ملی',
                value: <Masked kind="nationalCode" value={buyer.nationalCodeMasked} />,
              },
              {
                key: 'mobile',
                label: 'شماره موبایل',
                value: (
                  <span className="admin-inline">
                    {mobileDisplay}
                    <IconButton
                      icon="edit"
                      label="تغییر شماره موبایل"
                      onClick={() => setMobileOpen(true)}
                      variant="tonal"
                    />
                  </span>
                ),
              },
            ]}
          />
          <div className="admin-grid-2">
            <TextField
              error={form.formState.errors.firstNameFa?.message}
              label="نام"
              {...form.register('firstNameFa')}
            />
            <TextField
              error={form.formState.errors.lastNameFa?.message}
              label="نام خانوادگی"
              {...form.register('lastNameFa')}
            />
          </div>
          <Controller
            control={form.control}
            name="birthDate"
            render={({ field, fieldState }) => (
              <AdminSolarDateField
                error={fieldState.error?.message}
                label="تاریخ تولد"
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
          <Controller
            control={form.control}
            name="provinceId"
            render={({ field: province }) => (
              <Controller
                control={form.control}
                name="cityId"
                render={({ field: city }) => (
                  <GeographyFields
                    cityError={form.formState.errors.cityId?.message}
                    cityId={city.value}
                    idPrefix="buyer-edit"
                    onCityChange={city.onChange}
                    onProvinceChange={province.onChange}
                    provinceError={form.formState.errors.provinceId?.message}
                    provinceId={province.value}
                  />
                )}
              />
            )}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="buyer." />
          {mobileOpen ? (
            <MobileDialog
              buyer={buyer}
              onOpenChange={(next) => {
                if (!next) setMobileOpen(false);
              }}
            />
          ) : null}
        </>
      )}
    </CommandDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// B2 verifyBuyerIdentity

const identitySchema = z.object({
  documentType: z.string().min(1, 'نوع مدرک را انتخاب کنید.'),
  note: z.string().trim().max(500, 'حداکثر ۵۰۰ کاراکتر.'),
  result: z.enum(['VERIFIED', 'REJECTED']),
});
type IdentityValues = z.infer<typeof identitySchema>;

function documentLabel(code: string): string {
  const label = statusLabel('identityDocumentType', code);
  return label === 'نامشخص' ? 'مدرک دیگر' : label;
}

function IdentityDialog({
  buyer,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const offline = useSubmitBlocked();
  const policy = useCurrentPolicy<BuyerProvisioningPolicyVersion>('buyer-provisioning');
  const types = policy.data?.acceptedIdentityDocumentTypes ?? [];
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<IdentityValues>({
    defaultValues: { documentType: types[0] ?? '', note: '', result: 'VERIFIED' },
    resolver: zodResolver(identitySchema),
  });
  const command = useCommand<BuyerIdentityVerification, AdminBuyer>(
    'buyer.identity',
    (client, body, intent) =>
      client.post(buyerPath(buyer.buyerId, '/identity-verification'), { body, intent }),
    {},
  );

  function handle(outcome: CommandOutcome<AdminBuyer>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(applyFieldErrors(error, ['documentType', 'note', 'result'], form.setError));
    if (RELOAD_CODES.has(error.code)) void refresh();
  }

  async function submit(result: IdentityValues['result']) {
    const values = form.getValues();
    setUnmapped([]);
    const body: BuyerIdentityVerification = {
      documentType: values.documentType,
      expectedVersion: buyer.version,
      result,
      ...(values.note === '' ? {} : { note: values.note }),
    };
    const outcome = await command.submit(body);
    if (outcome.status === 'success') {
      await refresh();
      const { result } = outcome;
      toast.success({
        confirmedBy: result,
        title:
          result.data.status === 'ACTIVE' ? 'هویت خریدار تأیید و حساب فعال شد' : 'رد مدرک ثبت شد',
      });
      onOpenChange(false);
      return;
    }
    handle(outcome);
  }

  return (
    <Dialog
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      footer={
        <div className="saay-actions">
          <Button
            disabled={offline.blocked || command.state.status === 'pending'}
            loading={command.state.status === 'pending'}
            onClick={() => void form.handleSubmit(() => submit('VERIFIED'))()}
            type="button"
          >
            تأیید هویت
          </Button>
          <Button
            disabled={offline.blocked || command.state.status === 'pending'}
            loading={command.state.status === 'pending'}
            onClick={() => void form.handleSubmit(() => submit('REJECTED'))()}
            type="button"
            variant="danger"
          >
            رد مدرک
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      open
      stickyFooter
      title="بررسی مدرک شناسایی"
    >
      <form className="admin-form" noValidate onSubmit={(event) => event.preventDefault()}>
        <Controller
          control={form.control}
          name="documentType"
          render={({ field }) => (
            <SegmentedButtons
              label="نوع مدرک"
              onChange={field.onChange}
              options={types.map((code) => ({ key: code, label: documentLabel(code) }))}
              value={field.value}
            />
          )}
        />
        {form.formState.errors.documentType?.message === undefined ? null : (
          <span className="saay-field__error" role="alert">
            {form.formState.errors.documentType.message}
          </span>
        )}
        <AccordionPanel
          items={[
            {
              content: (
                <TextArea
                  error={form.formState.errors.note?.message}
                  label="یادداشت"
                  maxLength={500}
                  {...form.register('note')}
                />
              ),
              key: 'note',
              title: 'یادداشت بررسی',
            },
          ]}
          label="یادداشت بررسی"
        />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {command.state.status === 'error'
          ? (() => {
              const line = buyerGuidance(command.state.error);
              return line === undefined || line === null ? null : (
                <div className="saay-banner" data-tone="warning" role="status">
                  <div>{line}</div>
                </div>
              );
            })()
          : null}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// B3 changeBuyerMobile

const mobileSchema = z.object({
  newMobileE164: z
    .string()
    .transform(normalizeMobileField)
    .refine((value) => MOBILE_E164_PATTERN.test(value), 'شماره موبایل معتبر وارد کنید.'),
  reason: reasonSchema(),
});
type MobileValues = z.input<typeof mobileSchema>;

function MobileDialog({
  buyer,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const client = useApiClient();
  const currentDisplay =
    buyer.mobileE164 === undefined ? undefined : formatLocalMobileDisplay(buyer.mobileE164);
  const checkDuplicate = useCallback(
    async (raw: string, setError: (message: string) => void): Promise<void> => {
      const normalized = normalizeMobileField(raw);
      if (!MOBILE_E164_PATTERN.test(normalized)) {
        return;
      }
      if (buyer.mobileE164 !== undefined && normalized === buyer.mobileE164) {
        setError('این همان شمارهٔ فعلی است.');
        return;
      }
      const listed = await client.get<{ items: readonly { buyerId: string }[] }>('/admin/buyers', {
        query: { mobileE164: normalized },
      });
      const taken = listed.data.items.some((row) => row.buyerId !== buyer.buyerId);
      if (taken) {
        setError('این شماره قبلاً ثبت شده است.');
      }
    },
    [buyer.buyerId, buyer.mobileE164, client],
  );
  return (
    <CommandDialog<MobileValues, BuyerMobileChange, AdminBuyer>
      defaultValues={{ newMobileE164: '', reason: '' }}
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      fields={['newMobileE164', 'reason']}
      guidance={buyerGuidance}
      name="buyer.mobile"
      onError={async (error, form) => {
        if (error.code === 'MOBILE_ALREADY_IN_USE') {
          form.setError('newMobileE164', { message: error.messageFa, type: 'server' });
        }
        if (RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'شماره موبایل خریدار تغییر کرد' });
        onOpenChange(false);
      }}
      schema={mobileSchema as never}
      send={(client, body, intent) =>
        client.post(buyerPath(buyer.buyerId, '/mobile-changes'), { body, intent })
      }
      submitLabel="تغییر و خارج کردن از همهٔ دستگاه‌ها"
      title="تغییر شماره موبایل"
      toBody={(values) => ({
        expectedVersion: buyer.version,
        newMobileE164: values.newMobileE164,
        reason: values.reason,
      })}
    >
      {(form) => (
        <>
          {currentDisplay === undefined ? null : (
            <TextField label="شمارهٔ فعلی" ltr readOnly tabIndex={-1} value={currentDisplay} />
          )}
          <Controller
            control={form.control}
            name="newMobileE164"
            render={({ field, fieldState }) => (
              <TextField
                autoComplete="off"
                error={fieldState.error?.message}
                inputMode="tel"
                label="شماره موبایل جدید"
                ltr
                mask={MOBILE_MASK}
                maskRaw={field.value}
                onBlur={async () => {
                  field.onBlur();
                  await checkDuplicate(field.value, (message) => {
                    form.setError('newMobileE164', { message, type: 'validate' });
                  });
                }}
                onMaskRawChange={(raw) => {
                  field.onChange(raw);
                  form.clearErrors('newMobileE164');
                }}
              />
            )}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="buyer." />
        </>
      )}
    </CommandDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// B4 transitionBuyerStatus

type BuyerTarget = BuyerStatusTransition['targetStatus'];

const TRANSITION_COPY: Readonly<
  Record<'suspend' | 'reactivate' | 'close', { label: string; description: string }>
> = {
  close: {
    description: 'بستن حساب نهایی است؛ فقط وقتی بدهی و رزرو صفر باشد.',
    label: 'بستن حساب',
  },
  reactivate: {
    description: 'تعلیق برداشته می‌شود؛ سایر محدودیت‌های خرید می‌مانند.',
    label: 'رفع تعلیق',
  },
  suspend: {
    description: 'خرید و QR مسدود می‌شود؛ ورود و پرداخت اقساط ادامه دارد.',
    label: 'تعلیق خریدار',
  },
};

export function buyerCloseBlocked(buyer: AdminBuyer): {
  readonly blocked: boolean;
  readonly hint?: string;
} {
  const { outstandingPrincipalIrr, reservedPrincipalIrr } = buyer.card.totals;
  if (outstandingPrincipalIrr > 0 || reservedPrincipalIrr > 0) {
    const parts: string[] = [];
    if (outstandingPrincipalIrr > 0) {
      parts.push(`بدهی ${formatToman(outstandingPrincipalIrr)}`);
    }
    if (reservedPrincipalIrr > 0) {
      parts.push(`رزرو ${formatToman(reservedPrincipalIrr)}`);
    }
    return { blocked: true, hint: `بستن حساب با ${parts.join(' و ')} ممکن نیست.` };
  }
  return { blocked: false };
}

type BuyerTransition = { readonly key: keyof typeof TRANSITION_COPY; readonly target: BuyerTarget };

/** The transitions the contract allows from a status (AC §18.6.3); the server re-checks each. */
export function buyerTransitionsFrom(status: AdminBuyer['status']): readonly BuyerTransition[] {
  switch (status) {
    case 'ACTIVE':
      return [
        { key: 'suspend', target: 'SUSPENDED' },
        { key: 'close', target: 'CLOSED' },
      ];
    case 'SUSPENDED':
      return [
        { key: 'reactivate', target: 'ACTIVE' },
        { key: 'close', target: 'CLOSED' },
      ];
    case 'PENDING_VERIFICATION':
      return [{ key: 'close', target: 'CLOSED' }];
    default:
      return [];
  }
}

function TransitionDialog({
  buyer,
  onOpenChange,
  transition,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
  readonly transition: BuyerTransition;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const copy = TRANSITION_COPY[transition.key];
  return (
    <ReasonDialog<AdminBuyer>
      description={copy.description}
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      guidance={buyerGuidance}
      name={`buyer.status.${transition.target}`}
      onDone={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: `وضعیت خریدار: ${statusLabel('buyerStatus', result.data.status)}`,
        });
        onOpenChange(false);
      }}
      onError={async (error) => {
        if (isApiError(error) && RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      reasonLabel="دلیل تغییر وضعیت"
      send={(client, reason, intent) =>
        client.post(buyerPath(buyer.buyerId, '/status-transitions'), {
          body: {
            expectedVersion: buyer.version,
            reason,
            targetStatus: transition.target,
          } satisfies BuyerStatusTransition,
          intent,
        })
      }
      submitLabel={copy.label}
      title={copy.label}
      variant={transition.target === 'ACTIVE' ? 'primary' : 'danger'}
    />
  );
}

type LifecycleOpen =
  | { readonly kind: 'edit' }
  | { readonly kind: 'identity' }
  | { readonly kind: 'mobile' }
  | { readonly kind: 'transition'; readonly transition: BuyerTransition };

/** «وضعیت و مدارک خریدار»: the lifecycle commands the caller may take (`BUYER_MANAGE`). */
export function BuyerLifecycleActions({
  buyer,
  headlineOnly = false,
  initialOpen,
  onInitialOpenHandled,
  self,
  withOverflowMenu = false,
}: {
  readonly buyer: AdminBuyer;
  readonly headlineOnly?: boolean;
  readonly initialOpen?: 'identity' | 'suspend' | null;
  readonly onInitialOpenHandled?: () => void;
  readonly self: AdminSelf;
  readonly withOverflowMenu?: boolean;
}) {
  const [open, setOpen] = useState<LifecycleOpen | null>(null);
  useEffect(() => {
    if (initialOpen === undefined || initialOpen === null) {
      return;
    }
    if (initialOpen === 'identity' && buyer.status === 'PENDING_VERIFICATION') {
      setOpen({ kind: 'identity' });
    }
    if (initialOpen === 'suspend') {
      const transitions = canUse(self, 'transitionBuyerStatus')
        ? buyerTransitionsFrom(buyer.status)
        : [];
      const suspend = transitions.find((item) => item.target === 'SUSPENDED');
      const reactivate = transitions.find((item) => item.target === 'ACTIVE');
      const transition = suspend ?? reactivate;
      if (transition !== undefined) {
        setOpen({ kind: 'transition', transition });
      }
    }
    onInitialOpenHandled?.();
  }, [buyer.status, initialOpen, onInitialOpenHandled, self]);
  const closed = buyer.status === 'CLOSED';
  const canEdit = !closed && canUse(self, 'updateBuyer');
  const canIdentity =
    buyer.status === 'PENDING_VERIFICATION' && canUse(self, 'verifyBuyerIdentity');
  const canMobile = !closed && canUse(self, 'changeBuyerMobile');
  const transitions = canUse(self, 'transitionBuyerStatus')
    ? buyerTransitionsFrom(buyer.status)
    : [];
  const primaryTransition = transitions.find((item) => item.target === 'ACTIVE');
  const bodyTransitions = transitions.filter((item) => item.target !== 'ACTIVE');
  if (!canEdit && !canIdentity && !canMobile && transitions.length === 0) {
    return null;
  }
  const close = (next: boolean) => {
    if (!next) setOpen(null);
  };
  const dialogs = (
    <>
      {open?.kind === 'edit' ? <BuyerEditDialog buyer={buyer} onOpenChange={close} /> : null}
      {open?.kind === 'identity' ? <IdentityDialog buyer={buyer} onOpenChange={close} /> : null}
      {open?.kind === 'mobile' ? <MobileDialog buyer={buyer} onOpenChange={close} /> : null}
      {open?.kind === 'transition' ? (
        <TransitionDialog buyer={buyer} onOpenChange={close} transition={open.transition} />
      ) : null}
    </>
  );
  const overflowItems = (
    <>
      {canEdit ? (
        <MenuItem onSelect={() => setOpen({ kind: 'edit' })}>ویرایش مشخصات</MenuItem>
      ) : null}
      {canMobile ? (
        <MenuItem onSelect={() => setOpen({ kind: 'mobile' })}>تغییر شماره موبایل</MenuItem>
      ) : null}
      {!headlineOnly && canIdentity ? (
        <MenuItem onSelect={() => setOpen({ kind: 'identity' })}>بررسی مدرک شناسایی</MenuItem>
      ) : null}
      {bodyTransitions.map((transition) => {
        const copy = TRANSITION_COPY[transition.key];
        const closeBlock =
          transition.key === 'close' ? buyerCloseBlocked(buyer) : { blocked: false };
        return (
          <MenuItem
            destructive={transition.target !== 'ACTIVE'}
            disabled={closeBlock.blocked}
            key={transition.key}
            onSelect={() => setOpen({ kind: 'transition', transition })}
            {...(closeBlock.hint === undefined ? {} : { disabledReason: closeBlock.hint })}
          >
            {copy.label}
          </MenuItem>
        );
      })}
    </>
  );

  if (headlineOnly) {
    const hasPrimary = canIdentity || primaryTransition !== undefined;
    const hasOverflow = withOverflowMenu && (canEdit || canMobile || bodyTransitions.length > 0);
    if (!hasPrimary && !hasOverflow) {
      return dialogs;
    }
    return (
      <>
        {canIdentity ? (
          <Button onClick={() => setOpen({ kind: 'identity' })} size="sm">
            بررسی مدرک شناسایی
          </Button>
        ) : null}
        {primaryTransition === undefined ? null : (
          <Button
            onClick={() => setOpen({ kind: 'transition', transition: primaryTransition })}
            size="sm"
            variant="primary"
          >
            {TRANSITION_COPY[primaryTransition.key].label}
          </Button>
        )}
        {hasOverflow ? (
          <Menu
            label="عملیات خریدار"
            trigger={<IconButton icon="more" label="عملیات بیشتر" type="button" variant="tonal" />}
          >
            {overflowItems}
          </Menu>
        ) : null}
        {dialogs}
      </>
    );
  }
  return (
    <SheetSection id="buyer-lifecycle" title="وضعیت و مدارک">
      {buyer.statusReason === null ? null : (
        <p className="admin-muted">{`دلیل آخرین تغییر وضعیت: ${buyer.statusReason}`}</p>
      )}
      <div className="admin-actions">
        {canEdit ? (
          <Button onClick={() => setOpen({ kind: 'edit' })} size="sm" variant="secondary">
            ویرایش مشخصات
          </Button>
        ) : null}
        {canMobile ? (
          <Button onClick={() => setOpen({ kind: 'mobile' })} size="sm" variant="secondary">
            تغییر شماره موبایل
          </Button>
        ) : null}
        {bodyTransitions.map((transition) => {
          const copy = TRANSITION_COPY[transition.key];
          const closeBlock =
            transition.key === 'close' ? buyerCloseBlocked(buyer) : { blocked: false };
          return (
            <Button
              disabled={closeBlock.blocked}
              key={transition.key}
              onClick={() => setOpen({ kind: 'transition', transition })}
              size="sm"
              title={closeBlock.hint}
              variant={transition.target === 'ACTIVE' ? 'primary' : 'danger'}
            >
              {copy.label}
            </Button>
          );
        })}
      </div>
      {dialogs}
    </SheetSection>
  );
}

// ---------------------------------------------------------------------------------------------
// B5–B7 restrictions

const RESTRICTION_DURATION = ['MANUAL', 'DAYS_7', 'DAYS_30', 'DATE'] as const;

const restrictionSchema = z
  .object({
    duration: z.enum(RESTRICTION_DURATION),
    endsOn: z.string(),
    reason: reasonSchema(),
  })
  .superRefine((value, ctx) => {
    if (value.duration === 'DATE' && (value.endsOn === '' || value.endsOn <= tehranToday())) {
      ctx.addIssue({
        code: 'custom',
        message: 'تاریخ پایان باید در آینده باشد.',
        path: ['endsOn'],
      });
    }
  });
type RestrictionValues = z.infer<typeof restrictionSchema>;

function restrictionEndsOnDate(values: Pick<RestrictionValues, 'duration' | 'endsOn'>): string {
  switch (values.duration) {
    case 'MANUAL':
      return '';
    case 'DAYS_7':
      return addCalendarDays(tehranToday(), 7);
    case 'DAYS_30':
      return addCalendarDays(tehranToday(), 30);
    case 'DATE':
      return values.endsOn;
    default: {
      const _exhaustive: never = values.duration;
      return _exhaustive;
    }
  }
}

function restrictionEndSummary(values: Pick<RestrictionValues, 'duration' | 'endsOn'>): string {
  if (values.duration === 'MANUAL') {
    return 'تا رفع دستی';
  }
  const date = restrictionEndsOnDate(values);
  return date === '' ? '' : `تا ${formatLocalDate(date, 'long')}`;
}

function RestrictionCreateDialog({
  buyer,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  return (
    <CommandDialog<RestrictionValues, BuyerRestrictionCreate, BuyerRestriction>
      defaultValues={{ duration: 'MANUAL', endsOn: '', reason: '' }}
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      // The server names the `endsAt` timestamp; the person picked the day (`endsOn`).
      fieldAliases={{ endsAt: 'endsOn' }}
      fields={['reason']}
      guidance={buyerGuidance}
      name="buyer.restriction.create"
      onError={async (error) => {
        if (RELOAD_CODES.has(error.code) || error.code === 'RESTRICTION_ALREADY_ACTIVE') {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'محدودیت خرید ثبت شد' });
        onOpenChange(false);
      }}
      schema={restrictionSchema}
      send={(client, body, intent) =>
        client.post(buyerPath(buyer.buyerId, '/restrictions'), { body, intent })
      }
      submitLabel="ثبت محدودیت"
      title="محدودیت دستی خرید"
      toBody={(values) => {
        const endsOn = restrictionEndsOnDate(values);
        return {
          endsAt: endsOn === '' ? null : startOfBusinessDay(endsOn),
          reason: values.reason,
          restrictionType: 'PURCHASE_BLOCK',
        };
      }}
      variant="danger"
    >
      {(form) => {
        const duration = form.watch('duration');
        const endsOn = form.watch('endsOn');
        const summary = restrictionEndSummary({ duration, endsOn });
        return (
          <>
            <Controller
              control={form.control}
              name="duration"
              render={({ field }) => (
                <SegmentedControl
                  label="پایان محدودیت"
                  onChange={field.onChange}
                  options={[
                    { key: 'MANUAL', label: 'تا رفع دستی' },
                    { key: 'DAYS_7', label: '۷ روز' },
                    { key: 'DAYS_30', label: '۳۰ روز' },
                    { key: 'DATE', label: 'تاریخ…' },
                  ]}
                  value={field.value}
                />
              )}
            />
            {duration === 'DATE' ? (
              <Controller
                control={form.control}
                name="endsOn"
                render={({ field, fieldState }) => (
                  <AdminSolarDateField
                    error={fieldState.error?.message}
                    label="تاریخ پایان"
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            ) : null}
            {summary === '' ? null : <p className="admin-muted">{summary}</p>}
            <TextArea
              error={form.formState.errors.reason?.message}
              label="دلیل محدودیت"
              maxLength={1000}
              {...form.register('reason')}
            />
            <FormReasonChips form={form} intent="buyer." />
          </>
        );
      }}
    </CommandDialog>
  );
}

function RestrictionReleaseDialog({
  buyerId,
  onOpenChange,
  restriction,
}: {
  readonly buyerId: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly restriction: BuyerRestriction;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyerId);
  return (
    <ReasonDialog<BuyerRestriction>
      description="پس از رفع، اگر محدودیت دیگری نباشد خریدار دوباره می‌تواند خرید کند."
      guidance={buyerGuidance}
      name="buyer.restriction.release"
      onDone={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'محدودیت رفع شد' });
        onOpenChange(false);
      }}
      onError={async (error) => {
        if (isApiError(error) && RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      reasonLabel="دلیل رفع محدودیت"
      send={(client, reason, intent) =>
        client.post(`/admin/buyer-restrictions/${encodeURIComponent(restriction.id)}/release`, {
          body: { expectedVersion: restriction.version, reason },
          intent,
        })
      }
      submitLabel="رفع محدودیت"
      title="رفع محدودیت دستی"
    />
  );
}

/** B5 list with B6 and B7 (manual restrictions only; system ones show their source). */
export function RestrictionsSection({
  buyer,
  self,
}: {
  readonly buyer: AdminBuyer;
  readonly self: AdminSelf;
}) {
  const readable = canUse(self, 'listBuyerRestrictions');
  const restrictions = useBuyerRestrictions(buyer.buyerId, readable);
  const [creating, setCreating] = useState(false);
  const [releasing, setReleasing] = useState<BuyerRestriction | null>(null);
  if (!readable) {
    return null;
  }
  const rows = restrictions.data?.pages.flatMap((page) => page.items) ?? [];
  const canCreate =
    buyer.status !== 'CLOSED' &&
    canUse(self, 'createBuyerRestriction') &&
    !buyer.activeRestrictions.some((item) => item.sourceType === 'MANUAL');
  const canRelease = canUse(self, 'releaseBuyerRestriction');
  return (
    <SheetSection
      actions={
        canCreate ? (
          <Menu
            align="end"
            label="محدودیت خرید"
            trigger={<IconButton icon="more" label="محدودیت خرید" variant="standard" />}
          >
            <MenuItem onSelect={() => setCreating(true)}>افزودن محدودیت خرید</MenuItem>
          </Menu>
        ) : undefined
      }
      id="buyer-restrictions"
      title="محدودیت‌های خرید"
    >
      <QueryState
        empty={<EmptyState icon="check" title="محدودیتی ثبت نشده است" />}
        error={restrictions.error}
        isEmpty={rows.length === 0}
        onRetry={() => void restrictions.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری محدودیت‌ها…" lines={3} />}
        status={listStatus(restrictions)}
      >
        <Table
          caption="محدودیت‌های خرید خریدار"
          columns={[
            {
              header: 'منشأ',
              key: 'source',
              pin: 'start',
              render: (row) => statusLabel('buyerRestrictionSourceType', row.sourceType),
            },
            { header: 'دلیل', key: 'reason', render: (row) => row.reason },
            {
              header: 'از',
              key: 'starts',
              render: (row) => formatTimestamp(row.startsAt, { withTime: false }),
            },
            {
              header: 'تا',
              key: 'ends',
              render: (row) =>
                row.endsAt === null ? 'تا رفع' : formatTimestamp(row.endsAt, { withTime: false }),
            },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => <StatusChip enumName="buyerRestrictionStatus" value={row.status} />,
            },
            {
              detail: false,
              header: <span className="saay-visually-hidden">عملیات</span>,
              key: 'release',
              pin: 'end',
              render: (row) =>
                canRelease && row.sourceType === 'MANUAL' && row.status === 'ACTIVE' ? (
                  <Button onClick={() => setReleasing(row)} size="sm" variant="ghost">
                    رفع
                  </Button>
                ) : null,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {restrictions.isFetchNextPageError ? (
          <ErrorState
            error={restrictions.error}
            onRetry={() => void restrictions.fetchNextPage()}
          />
        ) : null}
        <LoadMore
          hasMore={restrictions.hasNextPage}
          loading={restrictions.isFetchingNextPage}
          onLoadMore={() => void restrictions.fetchNextPage()}
        />
      </QueryState>
      {creating ? (
        <RestrictionCreateDialog
          buyer={buyer}
          onOpenChange={(next) => {
            if (!next) setCreating(false);
          }}
        />
      ) : null}
      {releasing === null ? null : (
        <RestrictionReleaseDialog
          buyerId={buyer.buyerId}
          onOpenChange={(next) => {
            if (!next) setReleasing(null);
          }}
          restriction={releasing}
        />
      )}
    </SheetSection>
  );
}

// ---------------------------------------------------------------------------------------------
// B9 adjustBaseCredit, B10 adjustCreditAllocation

function baseCreditSchema(maxBaseCreditIrr: number | undefined) {
  return z
    .object({
      newApprovedLimitIrr: z
        .number()
        .int()
        .min(0)
        .nullable()
        .refine((value) => value !== null, 'سقف جدید را وارد کنید.'),
      reason: reasonSchema(),
    })
    .superRefine((value, ctx) => {
      if (
        maxBaseCreditIrr !== undefined &&
        value.newApprovedLimitIrr !== null &&
        value.newApprovedLimitIrr > maxBaseCreditIrr
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `بیش از حداکثر مجاز سیاست (${formatToman(maxBaseCreditIrr)}).`,
          path: ['newApprovedLimitIrr'],
        });
      }
    });
}
type BaseCreditValues = z.infer<ReturnType<typeof baseCreditSchema>>;

function BaseCreditDialog({
  base,
  buyer,
  onOpenChange,
}: {
  readonly base: AdminCreditAllocation;
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const self = useAdminSelf().data;
  const creditPolicy = useCurrentPolicy<CreditPolicyVersion>('credit');
  const policyMax = creditPolicy.data?.maxBaseCreditIrr;
  const limitHint =
    policyMax === undefined
      ? formatToman(base.approvedLimitIrr)
      : `${formatToman(base.approvedLimitIrr)} · ${formatToman(policyMax)}`;
  return (
    <CommandDialog<BaseCreditValues, BaseCreditAdjustment, CreditAllocationAdjusted>
      defaultValues={{ newApprovedLimitIrr: base.approvedLimitIrr, reason: '' }}
      description="تغییر سقف بلافاصله اعمال می‌شود. اگر سرور آن را برای تأیید بفرستد، تا تصمیم مدیر دیگر اعمال نمی‌شود. قراردادهای قبلی تغییر نمی‌کنند."
      eyebrow={`${buyer.firstNameFa} ${buyer.lastNameFa}`}
      fields={['newApprovedLimitIrr', 'reason']}
      guidance={buyerGuidance}
      name="buyer.base-credit"
      onApprovalPending={refresh}
      onError={async (error, form) => {
        if (error.code === 'CREDIT_POLICY_LIMIT_EXCEEDED') {
          form.setError('newApprovedLimitIrr', { message: error.messageFa, type: 'server' });
        }
        if (RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: `از ${formatToman(result.data.previousLimitIrr)} به ${formatToman(result.data.newLimitIrr)}`,
          title: 'اعتبار پایه اصلاح شد',
        });
        onOpenChange(false);
      }}
      schema={baseCreditSchema(policyMax)}
      send={(client, body, intent) =>
        client.post(buyerPath(buyer.buyerId, '/base-credit-adjustments'), { body, intent })
      }
      submitLabel={approvalSubmitLabel(self ?? {})}
      title="اصلاح اعتبار پایه"
      toBody={(values) =>
        values.newApprovedLimitIrr === base.approvedLimitIrr
          ? null
          : {
              expectedVersion: buyer.version,
              newApprovedLimitIrr: values.newApprovedLimitIrr as number,
              reason: values.reason,
            }
      }
    >
      {(form) => (
        <>
          {/* A separate line: for large amounts the field's own hint turns into the amount in words. */}
          <p className="admin-muted">{`فعلی · حداکثر مجاز — ${limitHint}`}</p>
          <Controller
            control={form.control}
            name="newApprovedLimitIrr"
            render={({ field, fieldState }) => (
              <MoneyField
                error={fieldState.error?.message}
                label="اعتبار پایه"
                onChangeRials={field.onChange}
                valueRials={field.value}
              />
            )}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="buyer." />
        </>
      )}
    </CommandDialog>
  );
}

const allocationSchema = z.object({
  amountIrr: z
    .number()
    .int()
    .min(1, 'مبلغ تغییر را وارد کنید.')
    .nullable()
    .refine((value) => value !== null && value > 0, 'مبلغ تغییر را وارد کنید.'),
  direction: z.enum(['INCREASE', 'DECREASE']),
  reason: reasonSchema(),
});
type AllocationValues = z.infer<typeof allocationSchema>;

function AllocationDialog({
  allocation,
  buyer,
  onOpenChange,
}: {
  readonly allocation: AdminCreditAllocation;
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  const self = useAdminSelf().data;
  return (
    <CommandDialog<AllocationValues, CreditAllocationAdjustmentRequest, CreditAllocationAdjusted>
      defaultValues={{ amountIrr: null, direction: 'INCREASE', reason: '' }}
      description="کاهش زیر مبلغ مصرف‌شده مجاز است و اعتبار آزاد صفر می‌شود؛ افزایش سقف تعهد برنامه و سیاست اعتبار را بررسی می‌کند."
      eyebrow={allocation.titleFa}
      // The server names the signed `deltaIrr`; the person typed the amount (`amountIrr`).
      fieldAliases={{ deltaIrr: 'amountIrr' }}
      fields={['reason']}
      guidance={buyerGuidance}
      name="buyer.allocation-adjustment"
      onApprovalPending={refresh}
      onError={async (error) => {
        if (RELOAD_CODES.has(error.code)) await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: `از ${formatToman(result.data.previousLimitIrr)} به ${formatToman(result.data.newLimitIrr)}`,
          title: 'تخصیص اعتبار اصلاح شد',
        });
        onOpenChange(false);
      }}
      schema={allocationSchema}
      send={(client, body, intent) =>
        client.post(`/admin/credit-allocations/${encodeURIComponent(allocation.id)}/adjustments`, {
          body,
          intent,
        })
      }
      submitLabel={approvalSubmitLabel(self ?? {})}
      title="اصلاح تخصیص اعتبار اضافی"
      toBody={(values) => ({
        // The sign is the chosen direction; the amount is the operator's input, not a computed figure.
        deltaIrr:
          values.direction === 'INCREASE'
            ? (values.amountIrr as number)
            : -(values.amountIrr as number),
        reason: values.reason,
      })}
    >
      {(form) => (
        <>
          <p className="admin-muted">{`سقف فعلی این تخصیص: ${formatToman(allocation.approvedLimitIrr)}`}</p>
          <Controller
            control={form.control}
            name="direction"
            render={({ field }) => (
              <SegmentedControl
                label="نوع تغییر"
                onChange={field.onChange}
                options={[
                  { key: 'INCREASE', label: 'افزایش سقف' },
                  { key: 'DECREASE', label: 'کاهش سقف' },
                ]}
                value={field.value}
              />
            )}
          />
          <Controller
            control={form.control}
            name="amountIrr"
            render={({ field, fieldState }) => (
              <MoneyField
                error={fieldState.error?.message}
                label="مبلغ تغییر"
                onChangeRials={field.onChange}
                valueRials={field.value}
              />
            )}
          />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="buyer." />
        </>
      )}
    </CommandDialog>
  );
}

/** The allocation that a credit action opens (base: B9; additional: B10). */
export type CreditActionTarget =
  | { readonly kind: 'base'; readonly allocation: AdminCreditAllocation }
  | { readonly kind: 'allocation'; readonly allocation: AdminCreditAllocation };

/** Which allocations the caller may adjust (`BUYER_CREDIT_MANAGE`, AC §18.6.6). */
export function creditActionFor(
  self: AdminSelf,
  buyer: AdminBuyer,
  allocation: AdminCreditAllocation,
): CreditActionTarget | null {
  if (buyer.status === 'CLOSED') return null;
  if (allocation.type === 'BASE') {
    return canUse(self, 'adjustBaseCredit') ? { allocation, kind: 'base' } : null;
  }
  return canUse(self, 'adjustCreditAllocation') &&
    (allocation.status === 'ACTIVE' || allocation.status === 'SCHEDULED')
    ? { allocation, kind: 'allocation' }
    : null;
}

export function CreditActionDialog({
  buyer,
  onOpenChange,
  target,
}: {
  readonly buyer: AdminBuyer;
  readonly onOpenChange: (open: boolean) => void;
  readonly target: CreditActionTarget;
}) {
  return target.kind === 'base' ? (
    <BaseCreditDialog base={target.allocation} buyer={buyer} onOpenChange={onOpenChange} />
  ) : (
    <AllocationDialog allocation={target.allocation} buyer={buyer} onOpenChange={onOpenChange} />
  );
}

/** Pending approvals on the buyer (AC §18.6.2 `pendingApprovals`): links to the inbox. */
export function BuyerPendingApprovals({ buyer }: { readonly buyer: AdminBuyer }) {
  if (buyer.pendingApprovals.length === 0) {
    return null;
  }
  return (
    <Banner tone="info">
      {`${formatCount(buyer.pendingApprovals.length)} درخواست در انتظار تأیید: `}
      {buyer.pendingApprovals.map((approval, index) => (
        <span key={approval.approvalRequestId}>
          {index === 0 ? '' : '، '}
          <Link to={`/approvals/${approval.approvalRequestId}`}>
            {statusLabel('approvalActionType', approval.actionType)}
          </Link>
        </span>
      ))}
    </Banner>
  );
}
